// ModalAdmin.jsx - Feature-rich with all styles editable
import React, { useState, useEffect } from 'react';
import ModalSection from '../components/ModalSection';
import axios from 'axios';
import configs from '../config/config';

const MODAL_PRESETS = {
  'split-image': {
    label: 'Split Image & Content',
    defaultConfig: {
      title: 'Summer Sale – Up to 70% Off',
      subtitle: 'Refresh your wardrobe with massive discounts!',
      background: '',
      textColor: '#111827',
      buttonText: 'Shop Now →',
      buttonBg: 'from-purple-600 to-pink-600',
      buttonTextColor: '#ffffff',
      imageUrl: '',
      showCountdown: false,
      titleStyle: { color: '#111827', fontSize: 'text-4xl md:text-5xl', fontWeight: 'font-black', fontFamily: 'font-sans' },
      subtitleStyle: { color: '#111827', fontSize: 'text-xl', fontWeight: 'font-normal', fontFamily: 'font-sans' },
      buttonStyle: { fontSize: 'text-lg', fontWeight: 'font-bold' },
    },
  },
  'centered-promo': {
    label: 'Centered Promotional',
    defaultConfig: {
      title: 'MEGA 50% OFF',
      subtitle: 'Everything must go – limited time!',
      background: 'from-orange-500 to-red-600',
      textColor: '#ffffff',
      buttonText: 'GRAB YOUR DISCOUNT',
      buttonBg: 'from-white to-gray-100',
      buttonTextColor: '#dc2626',
      titleStyle: { color: '#ffffff', fontSize: 'text-5xl md:text-6xl', fontWeight: 'font-black', fontFamily: 'font-sans' },
      subtitleStyle: { color: '#ffffff', fontSize: 'text-xl', fontWeight: 'font-normal', fontFamily: 'font-sans' },
      buttonStyle: { fontSize: 'text-xl', fontWeight: 'font-bold' },
    },
  },
  'newsletter': {
    label: 'Email Capture Newsletter',
    defaultConfig: {
      title: 'Get 15% Off Your First Order!',
      subtitle: 'Join now for exclusive deals & early access',
      background: 'from-blue-600 via-blue-700 to-cyan-600',
      textColor: '#ffffff',
      buttonText: 'Claim My 15% Discount',
      buttonBg: 'from-white to-gray-100',
      buttonTextColor: '#2563eb',
      titleStyle: { color: '#ffffff', fontSize: 'text-4xl md:text-5xl', fontWeight: 'font-black', fontFamily: 'font-sans' },
      subtitleStyle: { color: '#ffffff', fontSize: 'text-lg', fontWeight: 'font-normal', fontFamily: 'font-sans' },
      buttonStyle: { fontSize: 'text-lg', fontWeight: 'font-bold' },
    },
  },
  'spin-wheel': {
    label: 'Spin the Wheel Gamified',
    defaultConfig: {
      title: 'SPIN TO WIN!',
      subtitle: 'Try your luck – amazing discounts await!',
      background: 'from-emerald-600 to-teal-700',
      textColor: '#ffffff',
      buttonText: 'SPIN THE WHEEL',
      buttonBg: 'from-white to-gray-100',
      buttonTextColor: '#065f46',
      discountCodePrefix: 'YM',
      spinPrizes: ['10%', '15%', '20%', '25%', 'FREE SHIPPING'],
      wheelBackground: 'bg-white',
      wheelBorderColor: 'border-white/90',
      prizeEvenBg: 'bg-yellow-400 text-gray-900',
      prizeOddBg: 'bg-red-500 text-white',
      pointerColor: 'border-b-white',
      titleStyle: { color: '#ffffff', fontSize: 'text-4xl md:text-5xl', fontWeight: 'font-black', fontFamily: 'font-sans' },
      subtitleStyle: { color: '#ffffff', fontSize: 'text-lg', fontWeight: 'font-normal', fontFamily: 'font-sans' },
      buttonStyle: { fontSize: 'text-xl', fontWeight: 'font-bold' },
    },
  },
  'multi-product': {
    label: 'Multi-Product Showcase',
    defaultConfig: {
      title: 'Hot Deals – New Arrivals Sale',
      subtitle: 'Up to 60% off selected items • Free shipping over $50',
      background: 'from-rose-600 to-pink-600',
      textColor: '#ffffff',
      buttonText: 'Shop All Deals →',
      buttonBg: 'from-white to-gray-100',
      buttonTextColor: '#be185d',
      products: [],
      titleStyle: { color: '#ffffff', fontSize: 'text-4xl md:text-5xl', fontWeight: 'font-black', fontFamily: 'font-sans' },
      subtitleStyle: { color: '#ffffff', fontSize: 'text-lg', fontWeight: 'font-normal', fontFamily: 'font-sans' },
      buttonStyle: { fontSize: 'text-lg', fontWeight: 'font-bold' },
    },
  },
};

const ModalAdmin = ({ slug = '', onSave, onClose }) => {
  const [config, setConfig] = useState({
    isShowModal: false,
    preset: 'split-image',
    title: '',
    subtitle: '',
    background: '',
    textColor: '#111827',
    buttonText: '',
    buttonBg: '',
    buttonTextColor: '',
    overlayColor: 'black',
    overlayOpacity: 65,
    overlayBlur: 'md',
    imageUrl: '',
    discountCodePrefix: 'LUCKY',
    spinPrizes: ['10%', '15%', '20%', '25%'],
    wheelBackground: 'bg-white',
    wheelBorderColor: 'border-white/90',
    prizeEvenBg: 'bg-yellow-400 text-gray-900',
    prizeOddBg: 'bg-red-500 text-white',
    pointerColor: 'border-b-white',
    products: [],
    titleStyle: { color: '#111827', fontSize: 'text-4xl md:text-5xl', fontWeight: 'font-black', fontFamily: 'font-sans' },
    subtitleStyle: { color: '#111827', fontSize: 'text-xl', fontWeight: 'font-normal', fontFamily: 'font-sans' },
    buttonStyle: { fontSize: 'text-lg', fontWeight: 'font-bold' },
    devMode: false,
    customStyles: { container: { padding: 'py-12 md:py-16', background: 'bg-white', margin: 'mx-auto max-w-7xl' } },
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fullScreenPreview, setFullScreenPreview] = useState(false);

  const token = localStorage.getItem("token");

  useEffect(() => {
    const loadModalSettings = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        await axios.get(configs.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });

        let response;
        const isDuplicate = slug.includes('-');

        if (isDuplicate) {
          response = await axios.get(
            `${configs.API_URL}/api/pullCustomSettings/${slug}`,
            { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
          );
        } else {
          response = await axios.get(
            configs.API_URL + '/api/pullModalSettings',
            { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
          );
        }

        const dbData = isDuplicate
          ? (response.data.customSettings || {})
          : (response.data.modalSettings || {});

        applyDbData(dbData);
      } catch (error) {
        console.error("Failed to load modal settings:", error);
      } finally {
        setLoading(false);
      }
    };

    const applyDbData = (dbData) => {
      const presetKey = dbData.preset || 'split-image';
      const presetDefaults = MODAL_PRESETS[presetKey]?.defaultConfig || {};

      setConfig({
        isShowModal: dbData.isShowModal ?? false,
        preset: presetKey,
        title: dbData.title || presetDefaults.title || '',
        subtitle: dbData.subtitle || presetDefaults.subtitle || '',
        background: dbData.background || presetDefaults.background || '',
        textColor: dbData.textColor || presetDefaults.textColor || '#111827',
        buttonText: dbData.buttonText || presetDefaults.buttonText || '',
        buttonBg: dbData.buttonBg || presetDefaults.buttonBg || '',
        buttonTextColor: dbData.buttonTextColor || presetDefaults.buttonTextColor || '',
        overlayColor: dbData.overlayColor || 'black',
        overlayOpacity: dbData.overlayOpacity ?? 65,
        overlayBlur: dbData.overlayBlur || 'md',
        imageUrl: dbData.imageUrl || '',
        discountCodePrefix: dbData.discountCodePrefix || presetDefaults.discountCodePrefix || 'LUCKY',
        spinPrizes: dbData.spinPrizes || presetDefaults.spinPrizes || ['10%', '15%', '20%', '25%'],
        wheelBackground: dbData.wheelBackground || presetDefaults.wheelBackground || 'bg-white',
        wheelBorderColor: dbData.wheelBorderColor || presetDefaults.wheelBorderColor || 'border-white/90',
        prizeEvenBg: dbData.prizeEvenBg || presetDefaults.prizeEvenBg || 'bg-yellow-400 text-gray-900',
        prizeOddBg: dbData.prizeOddBg || presetDefaults.prizeOddBg || 'bg-red-500 text-white',
        pointerColor: dbData.pointerColor || presetDefaults.pointerColor || 'border-b-white',
        products: dbData.products || presetDefaults.products || [],
        titleStyle: dbData.titleStyle || presetDefaults.titleStyle || { color: '#111827', fontSize: 'text-4xl md:text-5xl', fontWeight: 'font-black', fontFamily: 'font-sans' },
        subtitleStyle: dbData.subtitleStyle || presetDefaults.subtitleStyle || { color: '#111827', fontSize: 'text-xl', fontWeight: 'font-normal', fontFamily: 'font-sans' },
        buttonStyle: dbData.buttonStyle || presetDefaults.buttonStyle || { fontSize: 'text-lg', fontWeight: 'font-bold' },
        devMode: dbData.devMode || false,
        customStyles: {
          container: {
            ...config.customStyles.container,
            ...(dbData.customStyles?.container || {}),
          },
        },
      });
    };

    loadModalSettings();
  }, [token, slug]);

  const saveModalSettings = async () => {
    if (!token) {
      alert("Please log in to save");
      return;
    }

    try {
      setSaving(true);
      await axios.get(configs.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });

      const xsrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1];

      const payload = { settings: config };

      const response = await axios.post(
        configs.API_URL + "/api/saveModalSettings" + (slug ? `/${slug}` : ''),
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-XSRF-TOKEN": decodeURIComponent(xsrfToken || ''),
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        }
      );

      if (response.data.status === 1) {
        alert("Modal settings saved successfully! 🎉");
        if (onSave) onSave(config);
      } else {
        alert("Save failed: " + (response.data.message || "Unknown error"));
      }
    } catch (error) {
      console.error("Save error:", error);
      alert("Failed to save modal settings");
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      setConfig(prev => ({ ...prev, imageUrl: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handlePresetChange = (newPreset) => {
    const defaults = MODAL_PRESETS[newPreset]?.defaultConfig || {};
    setConfig(prev => ({
      ...prev,
      preset: newPreset,
      title: defaults.title || prev.title,
      subtitle: defaults.subtitle || prev.subtitle,
      background: defaults.background || prev.background,
      textColor: defaults.textColor || prev.textColor,
      buttonText: defaults.buttonText || prev.buttonText,
      buttonBg: defaults.buttonBg || prev.buttonBg,
      buttonTextColor: defaults.buttonTextColor || prev.buttonTextColor,
      discountCodePrefix: defaults.discountCodePrefix || prev.discountCodePrefix,
      spinPrizes: defaults.spinPrizes || prev.spinPrizes,
      wheelBackground: defaults.wheelBackground || prev.wheelBackground,
      wheelBorderColor: defaults.wheelBorderColor || prev.wheelBorderColor,
      prizeEvenBg: defaults.prizeEvenBg || prev.prizeEvenBg,
      prizeOddBg: defaults.prizeOddBg || prev.prizeOddBg,
      pointerColor: defaults.pointerColor || prev.pointerColor,
      products: defaults.products || prev.products,
      titleStyle: defaults.titleStyle || prev.titleStyle,
      subtitleStyle: defaults.subtitleStyle || prev.subtitleStyle,
      buttonStyle: defaults.buttonStyle || prev.buttonStyle,
    }));
  };

  const updateStyle = (styleKey, field, value) => {
    setConfig(prev => ({
      ...prev,
      [styleKey]: { ...prev[styleKey], [field]: value },
    }));
  };

  const updateProduct = (index, field, value) => {
    const newProducts = [...config.products];
    newProducts[index] = { ...newProducts[index], [field]: value };
    setConfig(prev => ({ ...prev, products: newProducts }));
  };

  const addProduct = () => {
    setConfig(prev => ({
      ...prev,
      products: [
        ...prev.products,
        { name: 'New Product', price: '$0.00', old: '', img: '', disc: '' },
      ],
    }));
  };

  const removeProduct = (index) => {
    const newProducts = config.products.filter((_, i) => i !== index);
    setConfig(prev => ({ ...prev, products: newProducts }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <p className="text-xl text-gray-600">Loading modal settings...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex">

      {/* Preview */}
      <div className="flex-1 relative">
        <div className="absolute inset-0 flex flex-col">
          <div className="bg-white border-b px-6 py-4 flex justify-between items-center shadow-sm z-10">
            <h1 className="text-2xl font-bold text-gray-900">Marketing Modal Editor</h1>
            <button
              onClick={() => setFullScreenPreview(true)}
              className="bg-indigo-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-indigo-700 shadow transform hover:scale-105 transition"
            >
              Full Screen Preview
            </button>
          </div>
          <div className="flex-1 overflow-auto bg-gradient-to-b from-gray-50 to-gray-100 p-8">
            <ModalSection config={config} />
          </div>
        </div>
      </div>

      {/* Editor Sidebar */}
      <div className="w-96 bg-white border-l shadow-2xl overflow-y-auto">
        <div className="p-6 space-y-6">

          <h2 className="text-2xl font-bold text-gray-900">Modal Settings</h2>

          {/* Visibility */}
          <div className="flex items-center justify-between bg-indigo-50 p-4 rounded-xl">
            <span className="font-medium">Show modal to visitors</span>
            <input
              type="checkbox"
              checked={config.isShowModal}
              onChange={e => setConfig(prev => ({ ...prev, isShowModal: e.target.checked }))}
              className="h-6 w-6 text-indigo-600 rounded"
            />
          </div>

          {/* Preset */}
          <div>
            <label className="block font-medium mb-2">Design Preset</label>
            <select
              value={config.preset}
              onChange={e => handlePresetChange(e.target.value)}
              className="w-full border rounded-lg px-4 py-3"
            >
              {Object.entries(MODAL_PRESETS).map(([key, p]) => (
                <option key={key} value={key}>{p.label}</option>
              ))}
            </select>
          </div>

          {/* Common Fields – Expanded for feature-rich editing */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Title</label>
              <input
                value={config.title}
                onChange={e => setConfig(prev => ({ ...prev, title: e.target.value }))}
                className="w-full border rounded-lg px-4 py-3"
              />
            </div>
            <div className="space-y-3">
              <h4 className="font-medium text-sm">Title Styling</h4>
              <input
                type="color"
                value={config.titleStyle.color}
                onChange={e => updateStyle('titleStyle', 'color', e.target.value)}
                className="h-10 w-full rounded border"
              />
              <select
                value={config.titleStyle.fontSize}
                onChange={e => updateStyle('titleStyle', 'fontSize', e.target.value)}
                className="w-full border rounded-lg px-4 py-3"
              >
                <option value="text-3xl md:text-4xl">3XL-4XL</option>
                <option value="text-4xl md:text-5xl">4XL-5XL</option>
                <option value="text-5xl md:text-6xl">5XL-6XL</option>
                <option value="text-6xl md:text-7xl">6XL-7XL</option>
              </select>
              <select
                value={config.titleStyle.fontWeight}
                onChange={e => updateStyle('titleStyle', 'fontWeight', e.target.value)}
                className="w-full border rounded-lg px-4 py-3"
              >
                <option value="font-normal">Normal</option>
                <option value="font-semibold">Semibold</option>
                <option value="font-bold">Bold</option>
                <option value="font-black">Black</option>
              </select>
              <select
                value={config.titleStyle.fontFamily}
                onChange={e => updateStyle('titleStyle', 'fontFamily', e.target.value)}
                className="w-full border rounded-lg px-4 py-3"
              >
                <option value="font-sans">Sans</option>
                <option value="font-serif">Serif</option>
                <option value="font-mono">Mono</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Subtitle</label>
              <textarea
                value={config.subtitle}
                onChange={e => setConfig(prev => ({ ...prev, subtitle: e.target.value }))}
                className="w-full border rounded-lg px-4 py-3 min-h-[80px]"
              />
            </div>
            <div className="space-y-3">
              <h4 className="font-medium text-sm">Subtitle Styling</h4>
              <input
                type="color"
                value={config.subtitleStyle.color}
                onChange={e => updateStyle('subtitleStyle', 'color', e.target.value)}
                className="h-10 w-full rounded border"
              />
              <select
                value={config.subtitleStyle.fontSize}
                onChange={e => updateStyle('subtitleStyle', 'fontSize', e.target.value)}
                className="w-full border rounded-lg px-4 py-3"
              >
                <option value="text-base">Base</option>
                <option value="text-lg">Large</option>
                <option value="text-xl">XL</option>
                <option value="text-2xl">2XL</option>
              </select>
              <select
                value={config.subtitleStyle.fontWeight}
                onChange={e => updateStyle('subtitleStyle', 'fontWeight', e.target.value)}
                className="w-full border rounded-lg px-4 py-3"
              >
                <option value="font-normal">Normal</option>
                <option value="font-medium">Medium</option>
                <option value="font-semibold">Semibold</option>
              </select>
              <select
                value={config.subtitleStyle.fontFamily}
                onChange={e => updateStyle('subtitleStyle', 'fontFamily', e.target.value)}
                className="w-full border rounded-lg px-4 py-3"
              >
                <option value="font-sans">Sans</option>
                <option value="font-serif">Serif</option>
                <option value="font-mono">Mono</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Main Text Color</label>
              <input
                type="color"
                value={config.textColor}
                onChange={e => setConfig(prev => ({ ...prev, textColor: e.target.value }))}
                className="h-10 w-full rounded border"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Background (gradient or solid)</label>
              <input
                value={config.background}
                onChange={e => setConfig(prev => ({ ...prev, background: e.target.value }))}
                className="w-full border rounded-lg px-4 py-3 font-mono text-sm"
                placeholder="from-purple-600 to-pink-600"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Button Text</label>
                <input
                  value={config.buttonText}
                  onChange={e => setConfig(prev => ({ ...prev, buttonText: e.target.value }))}
                  className="w-full border rounded-lg px-4 py-3"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Button Background</label>
                <input
                  value={config.buttonBg}
                  onChange={e => setConfig(prev => ({ ...prev, buttonBg: e.target.value }))}
                  className="w-full border rounded-lg px-4 py-3 font-mono text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Button Text Color</label>
              <input
                type="color"
                value={config.buttonTextColor}
                onChange={e => setConfig(prev => ({ ...prev, buttonTextColor: e.target.value }))}
                className="h-10 w-full rounded border"
              />
            </div>

            <div className="space-y-3">
              <h4 className="font-medium text-sm">Button Styling</h4>
              <select
                value={config.buttonStyle.fontSize}
                onChange={e => updateStyle('buttonStyle', 'fontSize', e.target.value)}
                className="w-full border rounded-lg px-4 py-3"
              >
                <option value="text-sm">Small</option>
                <option value="text-base">Base</option>
                <option value="text-lg">Large</option>
                <option value="text-xl">XL</option>
              </select>
              <select
                value={config.buttonStyle.fontWeight}
                onChange={e => updateStyle('buttonStyle', 'fontWeight', e.target.value)}
                className="w-full border rounded-lg px-4 py-3"
              >
                <option value="font-normal">Normal</option>
                <option value="font-medium">Medium</option>
                <option value="font-bold">Bold</option>
              </select>
            </div>
          </div>

          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium mb-1">Image (background or promo)</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
            />
            {config.imageUrl && (
              <img
                src={config.imageUrl}
                alt="preview"
                className="mt-3 w-full h-48 object-cover rounded-lg border"
              />
            )}
          </div>

          {/* Overlay Settings */}
          <div className="space-y-4 pt-6 border-t">
            <h4 className="font-medium text-gray-800">Overlay Settings</h4>

            <div>
              <label className="block text-sm font-medium mb-1">Overlay Color</label>
              <div className="flex gap-3">
                <input
                  type="color"
                  value={config.overlayColor}
                  onChange={e => setConfig(prev => ({ ...prev, overlayColor: e.target.value }))}
                  className="h-10 w-20 rounded border"
                />
                <input
                  value={config.overlayColor}
                  onChange={e => setConfig(prev => ({ ...prev, overlayColor: e.target.value }))}
                  className="flex-1 border rounded-lg px-3 py-2 font-mono text-sm"
                  placeholder="black or #000000 or rgba(0,0,0,0.7)"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Overlay Opacity ({config.overlayOpacity}%)</label>
              <input
                type="range"
                min="0"
                max="100"
                step="5"
                value={config.overlayOpacity}
                onChange={e => setConfig(prev => ({ ...prev, overlayOpacity: Number(e.target.value) }))}
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Backdrop Blur</label>
              <select
                value={config.overlayBlur}
                onChange={e => setConfig(prev => ({ ...prev, overlayBlur: e.target.value }))}
                className="w-full border rounded-lg px-4 py-3"
              >
                <option value="none">None</option>
                <option value="sm">Small</option>
                <option value="md">Medium</option>
                <option value="lg">Large</option>
                <option value="xl">Extra Large</option>
                <option value="2xl">2XL</option>
                <option value="3xl">3XL</option>
              </select>
            </div>
          </div>

          {/* Spin Wheel Extras */}
          {config.preset === 'spin-wheel' && (
            <div className="space-y-4 pt-6 border-t">
              <h4 className="font-medium text-gray-800">Spin Wheel Settings</h4>

              <div>
                <label className="block text-sm font-medium mb-1">Discount Prefix</label>
                <input
                  value={config.discountCodePrefix}
                  onChange={e => setConfig(prev => ({ ...prev, discountCodePrefix: e.target.value.toUpperCase() }))}
                  className="w-full border rounded-lg px-4 py-3 font-mono uppercase"
                  maxLength={10}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Prizes (comma separated)</label>
                <textarea
                  value={config.spinPrizes.join(', ')}
                  onChange={e => setConfig(prev => ({
                    ...prev,
                    spinPrizes: e.target.value.split(',').map(s => s.trim()).filter(Boolean),
                  }))}
                  className="w-full border rounded-lg px-4 py-3 min-h-[100px] text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Wheel Background</label>
                <input
                  value={config.wheelBackground}
                  onChange={e => setConfig(prev => ({ ...prev, wheelBackground: e.target.value }))}
                  className="w-full border rounded-lg px-4 py-3 font-mono text-sm"
                  placeholder="bg-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Wheel Border Color</label>
                <input
                  value={config.wheelBorderColor}
                  onChange={e => setConfig(prev => ({ ...prev, wheelBorderColor: e.target.value }))}
                  className="w-full border rounded-lg px-4 py-3 font-mono text-sm"
                  placeholder="border-white/90"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Even Prize Background</label>
                <input
                  value={config.prizeEvenBg}
                  onChange={e => setConfig(prev => ({ ...prev, prizeEvenBg: e.target.value }))}
                  className="w-full border rounded-lg px-4 py-3 font-mono text-sm"
                  placeholder="bg-yellow-400 text-gray-900"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Odd Prize Background</label>
                <input
                  value={config.prizeOddBg}
                  onChange={e => setConfig(prev => ({ ...prev, prizeOddBg: e.target.value }))}
                  className="w-full border rounded-lg px-4 py-3 font-mono text-sm"
                  placeholder="bg-red-500 text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Pointer Color</label>
                <input
                  value={config.pointerColor}
                  onChange={e => setConfig(prev => ({ ...prev, pointerColor: e.target.value }))}
                  className="w-full border rounded-lg px-4 py-3 font-mono text-sm"
                  placeholder="border-b-white"
                />
              </div>
            </div>
         
        )}

        {/* Multi-Product Extras */}
        {config.preset === 'multi-product' && (
          <div className="space-y-4 pt-6 border-t">
            <h4 className="font-medium text-gray-800">Products ({config.products.length})</h4>
            <button onClick={addProduct} className="text-indigo-600 hover:underline">+ Add Product</button>

            {config.products.map((p, i) => (
              <div key={i} className="border p-4 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span>Product {i + 1}</span>
                  <button onClick={() => removeProduct(i)} className="text-red-600 hover:underline">Remove</button>
                </div>

                <input
                  value={p.name}
                  onChange={e => updateProduct(i, 'name', e.target.value)}
                  className="w-full border rounded px-3 py-2"
                  placeholder="Product Name"
                />
                <input
                  value={p.price}
                  onChange={e => updateProduct(i, 'price', e.target.value)}
                  className="w-full border rounded px-3 py-2"
                  placeholder="Price"
                />
                <input
                  value={p.old}
                  onChange={e => updateProduct(i, 'old', e.target.value)}
                  className="w-full border rounded px-3 py-2"
                  placeholder="Old Price (for strike-through)"
                />
                <input
                  value={p.img}
                  onChange={e => updateProduct(i, 'img', e.target.value)}
                  className="w-full border rounded px-3 py-2"
                  placeholder="Image URL or base64"
                />
                <input
                  value={p.disc}
                  onChange={e => updateProduct(i, 'disc', e.target.value)}
                  className="w-full border rounded px-3 py-2"
                  placeholder="Discount %"
                />
              </div>
            ))}
          </div>
        )}

            <div className="mt-6">
  <label className="block text-sm font-medium mb-1">CTA Button Link (href)</label>
  <input
    value={config.ctaHref || ''}
    onChange={e => setConfig(prev => ({ ...prev, ctaHref: e.target.value }))}
    className="w-full border rounded-lg px-4 py-3 font-mono text-sm"
    placeholder="/shop/all   or   /products/summer-sale"
  />
  <p className="text-xs text-gray-500 mt-1">Used with navigate() — relative or absolute paths work</p>
</div>

        {/* Dev Mode */}
        <details className="bg-yellow-50 border border-yellow-200 rounded-xl p-5">
          <summary className="font-semibold text-yellow-900 cursor-pointer">Dev Mode – Container</summary>
          <div className="mt-4 flex justify-between items-center mb-4">
            <h3 className="font-semibold text-yellow-900">Enabled</h3>
            <button
              onClick={() => setConfig(prev => ({ ...prev, devMode: !prev.devMode }))}
              className={`relative inline-flex h-6 w-11 rounded-full transition ${config.devMode ? 'bg-yellow-600' : 'bg-gray-300'}`}
            >
              <span className={`inline-block h-4 w-4 rounded-full bg-white transition ${config.devMode ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
          {config.devMode && (
            <div className="space-y-3">
              <input
                value={config.customStyles.container.padding}
                onChange={e => setConfig(prev => ({
                  ...prev,
                  customStyles: { container: { ...prev.customStyles.container, padding: e.target.value } }
                }))}
                className="w-full px-3 py-2 border rounded-md text-sm"
                placeholder="py-12 md:py-16"
              />
              <input
                value={config.customStyles.container.background}
                onChange={e => setConfig(prev => ({
                  ...prev,
                  customStyles: { container: { ...prev.customStyles.container, background: e.target.value } }
                }))}
                className="w-full px-3 py-2 border rounded-md text-sm"
                placeholder="bg-white"
              />
              <input
                value={config.customStyles.container.margin}
                onChange={e => setConfig(prev => ({
                  ...prev,
                  customStyles: { container: { ...prev.customStyles.container, margin: e.target.value } }
                }))}
                className="w-full px-3 py-2 border rounded-md text-sm"
                placeholder="mx-auto max-w-7xl"
              />
            </div>
          )}
        </details>

        {/* Save */}
        <div className="sticky bottom-0 bg-white border-t p-6 shadow-lg -mx-6">
          <button
            onClick={saveModalSettings}
            disabled={saving}
            className={`w-full py-4 rounded-xl font-bold text-lg shadow-lg transition-all ${
              saving ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700 text-white hover:shadow-xl'
            }`}
          >
            {saving ? 'Saving...' : 'Save & Publish Modal'}
          </button>
        </div>
      </div>
    </div>

    {/* Full Screen Preview */}
    {fullScreenPreview && (
      <div className="fixed inset-0 bg-black/95 z-50 flex flex-col" onClick={() => setFullScreenPreview(false)}>
        <div className="bg-white/10 backdrop-blur px-6 py-4 flex justify-end">
          <button className="text-white text-4xl hover:text-gray-300">×</button>
        </div>
        <div className="flex-1 overflow-auto p-8" onClick={e => e.stopPropagation()}>
          <ModalSection config={config} />
        </div>
      </div>
    )}
  </div>
);
};

export default ModalAdmin;